import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { useState } from "react";
import { Layout } from "@/components/Layout";
import { SEOHead } from "@/components/SEOHead";
import { NumberRow } from "@/components/NumberPill";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { useAllGames, useResults } from "@/hooks/use-query-hooks";
import { GamePicker } from "@/components/GamePicker";
import { saveCheckedResult, type CheckedResult } from "@/lib/local-storage";
import { formatDate } from "@/lib/format";
import { CheckCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

type Search = { game?: string };

export const Route = createFileRoute("/tools/results-checker")({
  validateSearch: (s: Record<string, unknown>): Search => ({ game: typeof s.game === "string" ? s.game : undefined }),
  head: () => buildSeo({
    title: "Results Checker — Lottery Tools | USA Lottery Club",
    description: "Check your lottery numbers against the latest winning draws across US lottery games.",
    path: "/tools/results-checker",
  }),
  component: ResultsChecker,
});

function ResultsChecker() {
  const { game: initial } = Route.useSearch();
  const { data: games, isLoading } = useAllGames();
  const [selectedGame, setSelectedGame] = useState(initial || "");
  const [numbersInput, setNumbersInput] = useState("");
  const [bonusInput, setBonusInput] = useState("");
  const [checkResult, setCheckResult] = useState<{ matched: number[]; matchedBonus: number[]; drawDate: string; winNumbers: number[]; bonusNumbers: number[] } | null>(null);

  const game = games?.find((g) => g.slug === selectedGame);
  const { data: results } = useResults(game?.id, 1);
  const latestResult = results?.[0];

  function check() {
    if (!game || !latestResult) return;
    const userNums = numbersInput.split(/[,\s]+/).map(Number).filter((n) => !isNaN(n) && n > 0);
    const userBonus = bonusInput.split(/[,\s]+/).map(Number).filter((n) => !isNaN(n) && n > 0);
    const winNums = latestResult.winning_numbers || [];
    const winBonus = latestResult.bonus_numbers || [];
    const matched = userNums.filter((n) => winNums.includes(n));
    const matchedBonus = userBonus.filter((n) => winBonus.includes(n));
    setCheckResult({ matched, matchedBonus, drawDate: latestResult.draw_date, winNumbers: winNums, bonusNumbers: winBonus });
    const cr: CheckedResult = {
      id: crypto.randomUUID(),
      gameSlug: game.slug,
      gameName: game.name,
      drawDate: latestResult.draw_date,
      userNumbers: userNums,
      userBonusNumbers: userBonus,
      matchedNumbers: matched,
      matchedBonus,
      checkedAt: new Date().toISOString(),
    };
    saveCheckedResult(cr);
    toast.success(`${matched.length + matchedBonus.length} matches found!`);
  }

  return (
    <Layout>
      <SEOHead title="Results Checker — Lottery Tools" description="Check your lottery numbers against the latest winning draws." />
      <div className="container py-6 md:py-10 max-w-2xl">
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/tools">Tools</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>Results Checker</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <h1 className="font-display text-2xl md:text-3xl font-bold mb-6">
          <CheckCircle className="inline h-6 w-6 mr-2 text-primary" /> Lottery Results Checker Tool
        </h1>
        {isLoading ? <Loader2 className="h-6 w-6 animate-spin text-primary" /> : (
          <Card className="mb-6">
            <CardContent className="pt-6 space-y-4">
              <GamePicker games={games} selectedSlug={selectedGame} onSelect={(v) => { setSelectedGame(v); setCheckResult(null); }} />
              {game && (
                <>
                  <div>
                    <Label>Your Numbers (comma or space separated)</Label>
                    <Input placeholder={`Enter ${game.number_count_main} numbers...`} value={numbersInput} onChange={(e) => setNumbersInput(e.target.value)} />
                  </div>
                  {(game.number_count_bonus || 0) > 0 && (
                    <div>
                      <Label>Bonus Number(s)</Label>
                      <Input placeholder="Enter bonus number..." value={bonusInput} onChange={(e) => setBonusInput(e.target.value)} />
                    </div>
                  )}
                  {latestResult && <p className="text-xs text-muted-foreground">Checking against: {formatDate(latestResult.draw_date)} draw</p>}
                  <Button onClick={check} disabled={!numbersInput.trim()} className="w-full">
                    <CheckCircle className="h-4 w-4 mr-2" /> Check Numbers
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}
        {checkResult && (
          <Card>
            <CardContent className="pt-6">
              <h2 className="font-display font-bold text-lg mb-3">Results — {formatDate(checkResult.drawDate)}</h2>
              <div className="mb-4">
                <p className="text-sm text-muted-foreground mb-2">Winning Numbers:</p>
                <NumberRow numbers={checkResult.winNumbers} bonusNumbers={checkResult.bonusNumbers} size="md" />
              </div>
              <div className="mb-4">
                <p className="text-sm text-muted-foreground mb-2">Your Numbers:</p>
                <NumberRow
                  numbers={numbersInput.split(/[,\s]+/).map(Number).filter((n) => !isNaN(n) && n > 0)}
                  bonusNumbers={bonusInput.split(/[,\s]+/).map(Number).filter((n) => !isNaN(n) && n > 0)}
                  matchedNumbers={checkResult.matched}
                  matchedBonus={checkResult.matchedBonus}
                  size="md"
                />
              </div>
              <div className="rounded-lg bg-secondary p-4 text-center">
                <p className="font-display font-bold text-2xl text-primary">
                  {checkResult.matched.length + checkResult.matchedBonus.length} Match{checkResult.matched.length + checkResult.matchedBonus.length !== 1 ? "es" : ""}
                </p>
                <p className="text-sm text-muted-foreground">{checkResult.matched.length} main + {checkResult.matchedBonus.length} bonus</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}